import { useMemo } from 'react';

import type { Coverage1PWorkflowConfig } from '@/services/api/generated/firmsService';
import {
  type MatterServiceWorkflowResponse,
  useGetMatterServicesQuery,
} from '@/services/api/mattersService';

const COVERAGE_1P_WORKFLOW_CODE = 'coverage_1p_enabled' as const;
const VERIFY_WITH_CLIENT = 'VERIFY_WITH_CLIENT' as const;

const isCoverage1PConfig = (
  config: MatterServiceWorkflowResponse['config'],
): config is Coverage1PWorkflowConfig => {
  return !!config && 'open_1p_coverage' in config;
};

export interface UseCoverage1PVerificationPolicyOptions {
  matterId: string;
  skip?: boolean;
}

export interface UseCoverage1PVerificationPolicyResult {
  verifyWithClientEnabled: boolean;
  isLoading: boolean;
}

/**
 * Read the effective `coverage_1p_enabled` workflow config for a matter
 * and surface whether the firm/matter requires client verification before
 * opening first-party coverage claims.
 *
 * Uses the matter-services endpoint, which already resolves firm defaults
 * with per-matter overrides so we get the effective policy in one call.
 */
export const useCoverage1PVerificationPolicy = ({
  matterId,
  skip = false,
}: UseCoverage1PVerificationPolicyOptions): UseCoverage1PVerificationPolicyResult => {
  const { data: matterServices, isLoading } = useGetMatterServicesQuery(
    { matterId },
    { skip: skip || !matterId },
  );

  const verifyWithClientEnabled = useMemo((): boolean => {
    if (!matterServices) return false;

    for (const service of matterServices) {
      if (!service.is_active) continue;

      const workflow = service.workflows.find(
        (w) => w.workflow_code === COVERAGE_1P_WORKFLOW_CODE,
      );
      if (!workflow?.is_enabled) continue;

      if (!isCoverage1PConfig(workflow.config)) continue;

      return workflow.config.open_1p_coverage === VERIFY_WITH_CLIENT;
    }

    return false;
  }, [matterServices]);

  return { verifyWithClientEnabled, isLoading };
};
